import { useState, useEffect, useCallback } from "react";
import { putSectionToDocumentURL, putActiveSectionToDocumentURL, getSectionTypesAdminURL } from "../../utils/ConstantsURL";
import { toastAlert } from "../../utils/AlertContext";

export function useEditSection(token, section, open, refresh, close) {
  const [formData, setFormData] = useState({
    name_section: "",
    id_type_section: "",
    description: "",
    id_section: null,
  });
  const [sectionTypes, setSectionTypes] = useState([]);
  const [isSwitchOn, setIsSwitchOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [initialSwitchValue, setInitialSwitchValue] = useState(true);

  useEffect(() => {
    const fetchSectionTypes = async () => {
      try {
        const res = await fetch(getSectionTypesAdminURL(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSectionTypes(data);
        }
      } catch (err) {
        console.error("Error al cargar tipos de sección:", err);
      }
    };
    fetchSectionTypes();
  }, [token]);

  useEffect(() => {
    if (section && open) {
      setFormData({
        name_section: section.NOMBRE_SECCION || "",
        id_type_section: section.ID_TIPO_SECCION?.toString() || "",
        description: section.DESCRIPCION || "",
        id_section: section.ID_SECCION || null,
      });
      const activo =
        section.FLG_ACTIVO === 1 ||
        section.FLG_ACTIVO === "1" ||
        section.FLG_ACTIVO === true;
      setIsSwitchOn(activo);
      setInitialSwitchValue(activo);
      setErrors({});
    }
  }, [section, open]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((f) => ({ ...f, [name]: value }));
      if (errors[name]) {
        setErrors((errs) => ({ ...errs, [name]: null }));
      }
    },
    [errors]
  );

  const validateForm = useCallback(() => {
    const newErr = {};
    if (!formData.name_section.trim()) {
      newErr.name_section = "El nombre de sección es obligatorio";
    }
    if (!formData.id_type_section) {
      newErr.id_type_section = "El tipo de sección es obligatorio";
    }
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  }, [formData]);

  const updateActiveState = useCallback(
    async (activo) => {
      try {
        const res = await fetch(putActiveSectionToDocumentURL(), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id_section: formData.id_section,
            activo: activo ? 1 : 0,
          }),
        });
        const payload = await res.json();
        if (res.ok) {
          toastAlert(payload.message, "success", 4000);
          return true;
        } else {
          toastAlert(
            payload.message || "Error al cambiar el estado de la sección",
            "error",
            4000
          );
          return false;
        }
      } catch (err) {
        console.error("updateActiveState:", err);
        toastAlert("Error de conexión", "error", 4000);
        return false;
      }
    },
    [token, formData.id_section]
  );

  const handleSwitchChange = useCallback(
    async (e) => {
      const nuevo = e.target.checked;
      if (nuevo === initialSwitchValue) return;
      setSwitchLoading(true);
      setIsSwitchOn(nuevo);
  
      const ok = await updateActiveState(nuevo);
      if (!ok) setIsSwitchOn((prev) => !prev);
  
      setSwitchLoading(false);
      refresh();
      close();
    },
    [initialSwitchValue, updateActiveState, refresh, close]
  );
  

  const handleUpdate = useCallback(async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await fetch(putSectionToDocumentURL(), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name_section: formData.name_section,
          id_type_section: Number(formData.id_type_section),
          description: formData.description,
          id_section: Number(formData.id_section),
        }),
      });
      const payload = await res.json();

      if (res.ok) {
        let activeOk = true;

        if (isSwitchOn !== initialSwitchValue) {
          activeOk = await updateActiveState(isSwitchOn);
        }

        if (activeOk) {
          toastAlert("Sección actualizada con éxito", "success", 4000);
        }

        refresh();
        close();
      } else {
        toastAlert(
          payload.message || "Error al actualizar la sección",
          "error",
          4000
        );
      }
    } catch (err) {
      console.error("handleUpdate:", err);
      toastAlert("Error de conexión", "error", 4000);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    formData,
    isSwitchOn,
    validateForm,
    initialSwitchValue,
    updateActiveState,
    refresh,
    close,
  ]);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  return {
    formData,
    isSwitchOn,
    sectionTypes,
    errors,
    loading,
    switchLoading,
    handleChange,
    handleSwitchChange,
    handleUpdate,
    handleClose,
  };
}